import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import ProjectStats from "@/components/ProjectStats";
import {
  Search, Filter, Play, Download, CheckCircle, AlertTriangle,
  Edit3, Save, X, BookOpen, RefreshCw, ChevronDown, Loader2,
  Wand2, FileDown, ShieldCheck, Replace, ScanSearch
} from "lucide-react";

export default function TranslationEditor({ project, setProject }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [segments, setSegments] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);
  const [translateProgress, setTranslateProgress] = useState('');
  const [filter, setFilter] = useState({ status: '', search: '', file: '' });
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [termbase, setTermbase] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [showTermSidebar, setShowTermSidebar] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [fileNames, setFileNames] = useState([]);
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [replacing, setReplacing] = useState(false);
  const [hideTagOnly, setHideTagOnly] = useState(true);
  const [uploadingTm, setUploadingTm] = useState(false);
  const [extractingTerms, setExtractingTerms] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [showAuditPanel, setShowAuditPanel] = useState(false);
  const [autoFixing, setAutoFixing] = useState(false);
  const [autoFixProgress, setAutoFixProgress] = useState('');

  // Fetch project data
  useEffect(() => {
    if (!projectId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [segRes, countRes, termRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}/segments`),
          axios.get(`${API}/projects/${projectId}/segments/count`),
          axios.get(`${API}/projects/${projectId}/termbase`),
        ]);
        setSegments(segRes.data);
        setCounts(countRes.data);
        setTermbase(termRes.data);

        // Extract unique file names
        const fnames = [...new Set(segRes.data.map(s => s.file_name))];
        setFileNames(fnames);

        // Fetch project if not set
        if (!project) {
          const projRes = await axios.get(`${API}/projects/${projectId}`);
          setProject(projRes.data);
        }
      } catch (e) {
        toast.error("Failed to load project data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId, project, setProject]);

  // Fetch segments with filters
  const fetchSegments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.search) params.append('search', filter.search);
      if (filter.file) params.append('file_name', filter.file);
      params.append('limit', '500');

      const [segRes, countRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/segments?${params}`),
        axios.get(`${API}/projects/${projectId}/segments/count`),
      ]);
      setSegments(segRes.data);
      setCounts(countRes.data);
    } catch (e) {
      console.error("Failed to fetch segments:", e);
    }
  }, [projectId, filter]);

  useEffect(() => {
    if (!loading && projectId) {
      fetchSegments();
    }
  }, [filter, fetchSegments, loading, projectId]);

  // Translate all - now with background polling
  const handleTranslateAll = useCallback(async () => {
    setTranslating(true);
    setTranslateProgress('Starting translation...');
    try {
      const res = await axios.post(`${API}/projects/${projectId}/translate?translate_new=true&translate_fuzzy=true`);
      if (res.data.total === 0) {
        setTranslateProgress('No segments to translate');
        setTranslating(false);
        return;
      }
      toast.success(res.data.message);
      // Start polling for progress
      const pollInterval = setInterval(async () => {
        try {
          const progressRes = await axios.get(`${API}/projects/${projectId}/translate/progress`);
          const job = progressRes.data;
          if (job.status === 'running') {
            setTranslateProgress(`Translating: ${job.translated}/${job.total} segments (${job.progress_pct}%) — ${job.errors} errors`);
          } else if (job.status === 'done') {
            clearInterval(pollInterval);
            setTranslateProgress(`Complete: ${job.translated}/${job.total} segments translated`);
            toast.success(`Translation complete: ${job.translated} segments`);
            setTranslating(false);
            await fetchSegments();
            const projRes = await axios.get(`${API}/projects/${projectId}`);
            setProject(projRes.data);
          } else if (job.status === 'error' || job.status === 'cancelled') {
            clearInterval(pollInterval);
            setTranslateProgress(`Translation ${job.status}: ${job.translated}/${job.total} done`);
            setTranslating(false);
            await fetchSegments();
          }
          // Also refresh counts during translation
          const countRes = await axios.get(`${API}/projects/${projectId}/segments/count`);
          setCounts(countRes.data);
        } catch (e) {
          console.error("Poll error:", e);
        }
      }, 3000);
      // Store interval ID so we can clear it on unmount
      return () => clearInterval(pollInterval);
    } catch (e) {
      const msg = e.response?.data?.detail || "Translation failed";
      toast.error(msg);
      setTranslateProgress('Translation failed');
      setTranslating(false);
    }
  }, [projectId, fetchSegments, setProject]);

  // Cancel translation
  const handleCancelTranslation = useCallback(async () => {
    try {
      await axios.post(`${API}/projects/${projectId}/translate/cancel`);
      toast.success("Translation cancellation requested");
    } catch (e) {
      toast.error("Failed to cancel");
    }
  }, [projectId]);

  // Bulk find & replace
  const handleBulkReplace = useCallback(async () => {
    if (!findText.trim()) { toast.error("Enter text to find"); return; }
    setReplacing(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/bulk-replace`, {
        find_text: findText,
        replace_text: replaceText,
        target_only: true,
      });
      toast.success(res.data.message);
      if (res.data.replaced > 0) {
        await fetchSegments();
      }
    } catch (e) {
      toast.error("Replace failed");
    } finally {
      setReplacing(false);
    }
  }, [projectId, findText, replaceText, fetchSegments]);

  // Upload TM file to project
  const handleUploadTm = useCallback(async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingTm(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(f => formData.append('files', f));
      const res = await axios.post(`${API}/projects/${projectId}/upload-tm`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to upload TM");
    } finally {
      setUploadingTm(false);
      e.target.value = '';
    }
  }, [projectId]);

  // Auto-fix terms from TM (one-click: extract + audit + replace)
  const handleAutoFixTerms = useCallback(async () => {
    setAutoFixing(true);
    setAutoFixProgress('Starting auto-fix...');
    try {
      const res = await axios.post(`${API}/projects/${projectId}/auto-fix-terms`);
      toast.success(res.data.message);

      // Poll for progress
      const pollInterval = setInterval(async () => {
        try {
          const progressRes = await axios.get(`${API}/projects/${projectId}/auto-fix-terms/progress`);
          const job = progressRes.data;
          if (job.status === 'running') {
            setAutoFixProgress(`${job.step} (${job.fixed} fixed)`);
          } else if (job.status === 'done') {
            clearInterval(pollInterval);
            setAutoFixProgress(`Done: ${job.terms_extracted} terms extracted, ${job.fixed} segments fixed`);
            toast.success(`Auto-fix complete: ${job.fixed} segments corrected`);
            setAutoFixing(false);
            await fetchSegments();
            const termRes = await axios.get(`${API}/projects/${projectId}/termbase`);
            setTermbase(termRes.data);
            // Show replacements
            if (job.replacements && job.replacements.length > 0) {
              setAuditResult({
                total_mismatches: job.replacements.length,
                details: job.replacements.map(([wrong, correct, cn]) => ({
                  source: cn,
                  target: `${wrong} → ${correct}`,
                  missing_terms: [{ term_source: cn, term_target: `"${wrong}" replaced with "${correct}"` }],
                })),
              });
              setShowAuditPanel(true);
            }
          } else if (job.status === 'error') {
            clearInterval(pollInterval);
            setAutoFixProgress(`Error: ${job.message || 'Unknown error'}`);
            toast.error(job.message || 'Auto-fix failed');
            setAutoFixing(false);
          }
        } catch (e) {
          console.error("Poll error:", e);
        }
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-fix failed");
      setAutoFixing(false);
      setAutoFixProgress('');
    }
  }, [projectId, fetchSegments]);

  // Audit translations against term base
  const handleAuditTerms = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/projects/${projectId}/audit-terms`);
      setAuditResult(res.data);
      setShowAuditPanel(true);
      toast.success(res.data.message);
      await fetchSegments();
      const countRes = await axios.get(`${API}/projects/${projectId}/segments/count`);
      setCounts(countRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    }
  }, [projectId, fetchSegments]);

  // Translate single segment
  const handleTranslateSingle = useCallback(async (segmentId) => {
    try {
      const res = await axios.post(`${API}/projects/${projectId}/translate/${segmentId}`);
      setSegments(prev => prev.map(s => s.id === segmentId ? res.data : s));
      // Refresh counts
      const countRes = await axios.get(`${API}/projects/${projectId}/segments/count`);
      setCounts(countRes.data);
      toast.success("Segment translated");
    } catch (e) {
      toast.error("Failed to translate segment");
    }
  }, [projectId]);

  // Save segment edit
  const handleSaveEdit = useCallback(async (segmentId) => {
    try {
      const res = await axios.put(`${API}/projects/${projectId}/segments/${segmentId}`, {
        target: editValue,
        status: 'reviewed',
      });
      setSegments(prev => prev.map(s => s.id === segmentId ? res.data : s));
      setEditingId(null);
      setEditValue('');
      // Refresh counts
      const countRes = await axios.get(`${API}/projects/${projectId}/segments/count`);
      setCounts(countRes.data);
      toast.success("Segment updated");
    } catch (e) {
      toast.error("Failed to save segment");
    }
  }, [projectId, editValue]);

  // Validate
  const handleValidate = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/projects/${projectId}/validate`);
      setValidationResult(res.data);
      setShowExportPanel(true);
    } catch (e) {
      toast.error("Validation failed");
    }
  }, [projectId]);

  // Export as TMX (for Trados TM import)
  const handleExport = useCallback((reviewedOnly = false, format = 'sdltm') => {
    if (!projectId) {
      toast.error("No project ID found");
      return;
    }
    // Export as SDLTM (native Trados format) or TMX
    const exportUrl = `${API}/projects/${projectId}/export/${format}?reviewed_only=${reviewedOnly}`;
    
    // Use hidden iframe to force download without navigating away
    let iframe = document.getElementById('download-iframe');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'download-iframe';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }
    iframe.src = exportUrl;
    const formatLabel = format === 'sdltm' ? 'SDLTM' : 'TMX';
    toast.success(`${formatLabel} download started — add this as a TM in Trados Studio`);
  }, [projectId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="editor-loading">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 text-neon-cyan animate-spin" />
          <span className="text-[#94A3B8] font-ui">Loading project...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col" data-testid="translation-editor">
      {/* Top bar */}
      <div className="px-6 py-3 border-b border-[#2A2F3A] bg-surface-100/80 backdrop-blur-md">
        <ProjectStats project={project} counts={counts} />
      </div>

      {/* Toolbar */}
      <div className="px-6 py-3 border-b border-[#2A2F3A] bg-surface-200/50 flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Search segments..."
            value={filter.search}
            onChange={e => setFilter(prev => ({ ...prev, search: e.target.value }))}
            className="w-full pl-10 pr-3 py-1.5 bg-surface-100 border border-[#2A2F3A] focus:border-neon-cyan rounded-sm text-sm text-[#E0E0E0] font-mono placeholder:text-[#64748B] outline-none transition-colors duration-200"
            data-testid="search-input"
          />
        </div>

        {/* Status filter */}
        <select
          value={filter.status}
          onChange={e => setFilter(prev => ({ ...prev, status: e.target.value }))}
          className="px-3 py-1.5 bg-surface-100 border border-[#2A2F3A] rounded-sm text-sm text-[#E0E0E0] font-ui outline-none cursor-pointer"
          data-testid="status-filter"
        >
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="tm_100">TM 100%</option>
          <option value="tm_fuzzy">Fuzzy</option>
          <option value="ai_translated">AI Translated</option>
          <option value="reviewed">Reviewed</option>
        </select>

        {/* File filter */}
        {fileNames.length > 1 && (
          <select
            value={filter.file}
            onChange={e => setFilter(prev => ({ ...prev, file: e.target.value }))}
            className="px-3 py-1.5 bg-surface-100 border border-[#2A2F3A] rounded-sm text-sm text-[#E0E0E0] font-ui outline-none cursor-pointer"
            data-testid="file-filter"
          >
            <option value="">All Files</option>
            {fileNames.map(fn => (
              <option key={fn} value={fn}>{fn}</option>
            ))}
          </select>
        )}

        {/* Hide tag-only toggle */}
        <button
          onClick={() => setHideTagOnly(!hideTagOnly)}
          className={`px-3 py-1.5 rounded-sm text-xs font-ui transition-colors duration-200 border ${hideTagOnly ? 'text-neon-cyan border-neon-cyan/30 bg-neon-cyan/10' : 'text-[#64748B] border-[#2A2F3A] hover:text-[#E0E0E0]'}`}
          data-testid="hide-tags-toggle"
        >
          {hideTagOnly ? 'Tags Hidden' : 'Show All'}
        </button>

        <div className="flex-1" />

        {/* Actions */}
        <button
          onClick={() => setShowFindReplace(!showFindReplace)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-ui transition-colors duration-200 border ${showFindReplace ? 'text-neon-warning border-neon-warning/30 bg-neon-warning/10' : 'text-[#94A3B8] hover:text-[#E0E0E0] hover:bg-white/5 border-[#2A2F3A]'}`}
          data-testid="find-replace-btn"
        >
          <Replace className="w-4 h-4" strokeWidth={1.5} />
          Find & Replace
        </button>

        <button
          onClick={() => setShowTermSidebar(!showTermSidebar)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-sm text-[#94A3B8] hover:text-[#E0E0E0] hover:bg-white/5 border border-[#2A2F3A] text-sm font-ui transition-colors duration-200"
          data-testid="toggle-termbase-btn"
        >
          <BookOpen className="w-4 h-4" strokeWidth={1.5} />
          Terms
        </button>

        {/* TM Upload + Extract + Audit */}
        <div className="relative">
          <input
            id="tm-upload-input"
            type="file"
            className="hidden"
            accept=".sdlppx,.sdltm,.sdltb"
            multiple
            onChange={handleUploadTm}
            data-testid="tm-upload-input"
          />
          <button
            onClick={() => document.getElementById('tm-upload-input').click()}
            disabled={uploadingTm}
            className="flex items-center gap-2 px-3 py-1.5 rounded-sm text-[#94A3B8] hover:text-[#E0E0E0] hover:bg-white/5 border border-[#2A2F3A] text-sm font-ui transition-colors duration-200 disabled:opacity-50"
            data-testid="upload-tm-btn"
          >
            {uploadingTm ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" strokeWidth={1.5} />}
            {uploadingTm ? 'Importing...' : 'Upload TM'}
          </button>
        </div>

        <button
          onClick={handleAutoFixTerms}
          disabled={autoFixing}
          className="flex items-center gap-2 px-4 py-1.5 rounded-sm bg-neon-purple/10 hover:bg-neon-purple/20 text-[#7000FF] border border-[#7000FF]/30 hover:border-[#7000FF]/60 font-ui text-sm font-semibold uppercase tracking-wider transition-colors duration-200 disabled:opacity-50"
          data-testid="auto-fix-terms-btn"
          title="Extract terms from TM and auto-fix all translations"
        >
          {autoFixing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" strokeWidth={1.5} />}
          {autoFixing ? 'Fixing...' : 'Auto-Fix Terms'}
        </button>

        <button
          onClick={handleTranslateAll}
          disabled={translating}
          className="flex items-center gap-2 px-4 py-1.5 rounded-sm bg-neon-cyan/10 hover:bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/30 hover:border-neon-cyan/60 font-ui text-sm font-semibold uppercase tracking-wider transition-colors duration-200 disabled:opacity-50"
          data-testid="translate-all-btn"
        >
          {translating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Wand2 className="w-4 h-4" strokeWidth={1.5} />
          )}
          {translating ? 'Translating...' : 'Translate All'}
        </button>

        <button
          onClick={handleValidate}
          className="flex items-center gap-2 px-3 py-1.5 rounded-sm text-[#94A3B8] hover:text-[#E0E0E0] hover:bg-white/5 border border-[#2A2F3A] text-sm font-ui transition-colors duration-200"
          data-testid="validate-btn"
        >
          <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
          Validate
        </button>

        <button
          onClick={handleAuditTerms}
          className="flex items-center gap-2 px-3 py-1.5 rounded-sm text-[#94A3B8] hover:text-neon-warning hover:bg-neon-warning/10 border border-[#2A2F3A] hover:border-neon-warning/30 text-sm font-ui transition-colors duration-200"
          data-testid="audit-terms-btn"
          title="Audit translations against term base"
        >
          <ScanSearch className="w-4 h-4" strokeWidth={1.5} />
          Audit Terms
        </button>

        <button
          onClick={() => handleExport(false, 'sdltm')}
          className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/30 hover:border-neon-green/60 font-ui text-sm font-semibold uppercase tracking-wider transition-colors duration-200"
          data-testid="export-btn"
        >
          <FileDown className="w-4 h-4" strokeWidth={1.5} />
          Export SDLTM
        </button>
      </div>

      {/* Translation progress */}
      {translateProgress && (
        <div className="px-6 py-2 bg-surface-100 border-b border-[#2A2F3A] flex items-center gap-2">
          {translating && <div className="w-2 h-2 rounded-full bg-neon-cyan animate-pulse" />}
          <span className="text-sm font-mono text-neon-cyan flex-1">{translateProgress}</span>
          {translating && (
            <button
              onClick={handleCancelTranslation}
              className="px-3 py-1 rounded-sm bg-neon-error/10 text-neon-error border border-neon-error/30 text-xs font-ui font-semibold uppercase tracking-wider hover:bg-neon-error/20 transition-colors duration-200"
              data-testid="cancel-translate-btn"
            >
              Cancel
            </button>
          )}
          {!translating && (
            <button onClick={() => setTranslateProgress('')} className="text-[#64748B] hover:text-[#E0E0E0]">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {/* Auto-fix progress */}
      {autoFixProgress && (
        <div className="px-6 py-2 bg-surface-100 border-b border-[#2A2F3A] flex items-center gap-2">
          {autoFixing && <div className="w-2 h-2 rounded-full bg-[#7000FF] animate-pulse" />}
          <span className="text-sm font-mono text-[#7000FF] flex-1">{autoFixProgress}</span>
          {!autoFixing && (
            <button onClick={() => setAutoFixProgress('')} className="text-[#64748B] hover:text-[#E0E0E0]">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      )}


      {/* Validation panel */}
      {showExportPanel && validationResult && (
        <div className="px-6 py-3 bg-surface-200 border-b border-[#2A2F3A]" data-testid="validation-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {validationResult.valid ? (
                <CheckCircle className="w-5 h-5 text-neon-green" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-neon-error" />
              )}
              <span className="font-ui font-semibold text-sm text-[#E0E0E0]">
                {validationResult.valid ? 'Validation Passed' : 'Validation Issues Found'}
              </span>
            </div>
            <button onClick={() => setShowExportPanel(false)} className="text-[#64748B] hover:text-[#E0E0E0]">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-5 gap-3 text-xs font-mono mb-2">
            <Stat label="Untranslated" value={validationResult.stats.untranslated} color="#FFB800" />
            <Stat label="Broken Tags" value={validationResult.stats.broken_tags} color="#FF003C" />
            <Stat label="Over Limit" value={validationResult.stats.over_limit} color="#FFB800" />
            <Stat label="Placeholder Err" value={validationResult.stats.placeholder_mismatches} color="#FF003C" />
            <Stat label="Missing Terms" value={validationResult.stats.missing_terms} color="#FFB800" />
          </div>
          {validationResult.errors.length > 0 && (
            <div className="text-xs text-neon-error font-mono space-y-0.5 max-h-20 overflow-y-auto">
              {validationResult.errors.slice(0, 5).map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => handleExport(false, 'sdltm')}
              className="px-3 py-1 rounded-sm bg-neon-green/10 text-neon-green border border-neon-green/30 text-xs font-ui font-semibold uppercase tracking-wider"
              data-testid="export-all-btn"
            >
              Export SDLTM
            </button>
            <button
              onClick={() => handleExport(false, 'tmx')}
              className="px-3 py-1 rounded-sm bg-surface-300 text-[#94A3B8] border border-[#2A2F3A] text-xs font-ui font-medium uppercase tracking-wider"
              data-testid="export-tmx-btn"
            >
              Export TMX
            </button>
            <button
              onClick={() => handleExport(true, 'sdltm')}
              className="px-3 py-1 rounded-sm bg-surface-300 text-[#94A3B8] border border-[#2A2F3A] text-xs font-ui font-medium uppercase tracking-wider"
              data-testid="export-reviewed-btn"
            >
              Reviewed Only
            </button>
          </div>
        </div>
      )}

      {/* Find & Replace Panel */}
      {showFindReplace && (
        <div className="px-6 py-3 bg-surface-200 border-b border-[#2A2F3A]" data-testid="find-replace-panel">
          <div className="flex items-center gap-3">
            <div className="flex-1 flex items-center gap-2">
              <div className="relative flex-1 max-w-[300px]">
                <ScanSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" strokeWidth={1.5} />
                <input
                  type="text"
                  placeholder="Find in translations..."
                  value={findText}
                  onChange={e => setFindText(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 bg-surface-100 border border-[#2A2F3A] focus:border-neon-warning rounded-sm text-sm font-mono text-[#E0E0E0] placeholder:text-[#64748B] outline-none"
                  data-testid="find-input"
                />
              </div>
              <span className="text-[#64748B] text-sm">→</span>
              <div className="relative flex-1 max-w-[300px]">
                <input
                  type="text"
                  placeholder="Replace with..."
                  value={replaceText}
                  onChange={e => setReplaceText(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-100 border border-[#2A2F3A] focus:border-neon-green rounded-sm text-sm font-mono text-[#E0E0E0] placeholder:text-[#64748B] outline-none"
                  data-testid="replace-input"
                />
              </div>
            </div>
            <button
              onClick={handleBulkReplace}
              disabled={replacing || !findText.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-sm bg-neon-warning/10 hover:bg-neon-warning/20 text-neon-warning border border-neon-warning/30 font-ui text-sm font-semibold uppercase tracking-wider transition-colors duration-200 disabled:opacity-50"
              data-testid="replace-all-btn"
            >
              {replacing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Replace className="w-4 h-4" strokeWidth={1.5} />}
              Replace All
            </button>
            <button
              onClick={() => { if (findText.trim()) setFilter(prev => ({ ...prev, search: findText })); }}
              className="px-3 py-2 rounded-sm text-[#94A3B8] hover:text-[#E0E0E0] border border-[#2A2F3A] text-sm font-ui"
              data-testid="find-preview-btn"
            >
              Preview
            </button>
            <button onClick={() => setShowFindReplace(false)} className="text-[#64748B] hover:text-[#E0E0E0] p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[10px] font-ui text-[#64748B] mt-1.5">
            Replaces exact text matches in all German translations. Use "Preview" to filter segments containing the search term first.
          </p>
        </div>
      )}

      {/* Audit Results Panel */}
      {showAuditPanel && auditResult && (
        <div className="px-6 py-3 bg-surface-200 border-b border-[#2A2F3A] max-h-[300px] overflow-auto" data-testid="audit-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ScanSearch className="w-5 h-5 text-neon-warning" />
              <span className="font-ui font-semibold text-sm text-[#E0E0E0]">
                Term Audit: {auditResult.total_mismatches} segments with term issues
              </span>
              <span className="text-xs text-[#64748B]">
                ({auditResult.flagged} newly flagged)
              </span>
            </div>
            <button onClick={() => setShowAuditPanel(false)} className="text-[#64748B] hover:text-[#E0E0E0]">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-3">
            {auditResult.details?.slice(0, 30).map((item, idx) => (
              <div key={idx} className="p-3 bg-surface-100 rounded-sm border border-neon-warning/30">
                <div className="flex items-start gap-2 mb-2">
                  <span className="text-[10px] font-mono text-[#64748B] shrink-0">#{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[#94A3B8] mb-1 truncate" title={item.source}>
                      <span className="text-[#64748B]">ZH:</span> {item.source}
                    </div>
                    <div className="text-xs text-[#E0E0E0] truncate" title={item.target}>
                      <span className="text-[#64748B]">DE:</span> {item.target}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {item.missing_terms?.map((mt, i) => (
                    <div key={i} className="flex items-center gap-1 px-2 py-1 rounded bg-neon-warning/10 border border-neon-warning/40">
                      <span className="text-[10px] text-neon-warning font-medium">
                        "{mt.term_source}"
                      </span>
                      <span className="text-[10px] text-[#64748B]">→</span>
                      <span className="text-[10px] text-neon-green font-semibold">
                        "{mt.term_target}"
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {auditResult.details?.length > 30 && (
            <div className="mt-2 text-center text-xs text-[#64748B]">
              ... and {auditResult.details.length - 30} more segments with issues
            </div>
          )}
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Segments table */}
        <div className="flex-1 overflow-auto">
          {/* Table header */}
          <div className="sticky top-0 z-10 grid grid-cols-[40px_1fr_1fr_100px_80px_60px] gap-2 px-6 py-2 bg-surface-100 border-b border-[#2A2F3A] text-[10px] font-ui font-medium uppercase tracking-wider text-[#64748B]">
            <span>#</span>
            <span>Source (ZH-CN)</span>
            <span>Target (DE-DE)</span>
            <span>Status</span>
            <span>Match</span>
            <span>Actions</span>
          </div>

          {/* Segments */}
          <div className="divide-y divide-[#2A2F3A]/30">
            {segments
              .filter(seg => !hideTagOnly || !isTagOnlySegment(seg.source))
              .map((seg, idx) => (
              <SegmentRow
                key={seg.id}
                segment={seg}
                index={idx}
                isEditing={editingId === seg.id}
                editValue={editValue}
                onStartEdit={() => { setEditingId(seg.id); setEditValue(seg.target || ''); }}
                onEditChange={setEditValue}
                onSaveEdit={() => handleSaveEdit(seg.id)}
                onCancelEdit={() => { setEditingId(null); setEditValue(''); }}
                onTranslate={() => handleTranslateSingle(seg.id)}
                onSelect={() => { setSelectedSegment(seg); setShowTermSidebar(true); }}
                termbase={termbase}
              />
            ))}
          </div>

          {segments.length === 0 && !loading && (
            <div className="flex items-center justify-center py-20 text-[#64748B] font-ui text-sm">
              No segments match the current filter.
            </div>
          )}
        </div>

        {/* Term Base Sidebar */}
        {showTermSidebar && (
          <div className="w-[300px] flex-shrink-0 border-l border-[#2A2F3A] bg-surface-100 flex flex-col overflow-hidden" data-testid="term-sidebar">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2F3A]">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-[#E0E0E0]">Term Base</h3>
              <button onClick={() => setShowTermSidebar(false)} className="text-[#64748B] hover:text-[#E0E0E0]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-3 space-y-2">
              {selectedSegment && (
                <div className="mb-3 p-2 bg-surface-200 rounded-sm border border-[#2A2F3A]">
                  <p className="text-[10px] font-ui uppercase tracking-wider text-[#64748B] mb-1">Active Segment</p>
                  <p className="text-xs font-mono text-neon-cyan truncate">{selectedSegment.source}</p>
                </div>
              )}
              {termbase.length === 0 ? (
                <p className="text-xs text-[#64748B] font-ui text-center py-4">No terms in term base.</p>
              ) : (
                termbase.map(term => {
                  const isRelevant = selectedSegment && selectedSegment.source.includes(term.source_term);
                  return (
                    <div
                      key={term.id}
                      className={`p-2 rounded-sm border transition-colors duration-200 ${
                        isRelevant
                          ? 'border-neon-cyan/30 bg-neon-cyan/5'
                          : 'border-[#2A2F3A] bg-surface-200/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-[#E0E0E0]">{term.source_term}</span>
                        <span className="text-[#64748B] text-xs">→</span>
                        <span className="text-xs font-mono text-neon-green">{term.target_term}</span>
                      </div>
                      {term.domain && (
                        <span className="text-[9px] font-ui uppercase tracking-wider text-[#64748B] px-1.5 py-0.5 bg-surface-300/50 rounded-sm">
                          {term.domain}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div className="p-3 border-t border-[#2A2F3A]">
              <button
                onClick={() => navigate(`/termbase/${projectId}`)}
                className="w-full py-2 text-center text-xs font-ui font-medium uppercase tracking-wider text-[#94A3B8] hover:text-neon-cyan border border-[#2A2F3A] hover:border-neon-cyan/30 rounded-sm transition-colors duration-200"
                data-testid="manage-termbase-btn"
              >
                Manage Term Base
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function isTagOnlySegment(source) {
  if (!source) return true;
  // Remove XML tags and check if anything remains
  const stripped = source.replace(/<[^>]+\/?>/g, '').trim();
  return stripped.length === 0;
}

function SegmentRow({ segment, index, isEditing, editValue, onStartEdit, onEditChange, onSaveEdit, onCancelEdit, onTranslate, onSelect, termbase }) {
  const statusClass = {
    tm_100: 'segment-tm-100',
    tm_fuzzy: 'segment-tm-fuzzy',
    ai_translated: 'segment-ai',
    new: 'segment-new',
    reviewed: 'segment-tm-100',
    confirmed: 'segment-tm-100',
  }[segment.status] || 'segment-new';

  const hasFlags = segment.flags && segment.flags.length > 0;
  if (hasFlags) {
    // Override with flagged style
  }

  const badgeClass = {
    tm_100: 'badge-tm100',
    tm_fuzzy: 'badge-fuzzy',
    ai_translated: 'badge-ai',
    new: 'badge-new',
    reviewed: 'badge-tm100',
    confirmed: 'badge-tm100',
  }[segment.status] || 'badge-new';

  const statusLabel = {
    tm_100: 'TM 100%',
    tm_fuzzy: 'Fuzzy',
    ai_translated: 'AI',
    new: 'New',
    reviewed: 'Reviewed',
    confirmed: 'Confirmed',
  }[segment.status] || segment.status;

  // Check if any term base entries are relevant
  const relevantTerms = termbase.filter(t => segment.source.includes(t.source_term));
  const missingTerms = relevantTerms.filter(t => segment.target && !segment.target.includes(t.target_term));

  return (
    <div
      className={`editor-row grid grid-cols-[40px_1fr_1fr_100px_80px_60px] gap-2 px-6 py-2.5 items-start ${statusClass} ${hasFlags ? 'segment-flagged' : ''}`}
      data-testid={`segment-row-${segment.id}`}
      onClick={onSelect}
    >
      {/* Index */}
      <span className="text-xs font-mono text-[#64748B] pt-1">{index + 1}</span>

      {/* Source */}
      <div className="min-w-0">
        <p className="text-sm font-mono text-[#E0E0E0] break-words leading-relaxed">{segment.source}</p>
        {segment.context && (
          <p className="text-[10px] font-ui text-[#64748B] mt-1 italic">{segment.context}</p>
        )}
      </div>

      {/* Target */}
      <div className="min-w-0">
        {isEditing ? (
          <div className="flex flex-col gap-1.5">
            <textarea
              value={editValue}
              onChange={e => onEditChange(e.target.value)}
              className="w-full p-2 bg-surface-100 border border-neon-cyan/50 rounded-sm text-sm font-mono text-[#E0E0E0] resize-none outline-none min-h-[60px]"
              autoFocus
              data-testid="edit-textarea"
            />
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-[#64748B]">
                {editValue.length} chars
                {segment.char_limit > 0 && ` / ${segment.char_limit} limit`}
              </span>
              <div className="flex-1" />
              <button
                onClick={(e) => { e.stopPropagation(); onSaveEdit(); }}
                className="p-1 rounded-sm bg-neon-green/10 text-neon-green hover:bg-neon-green/20"
                data-testid="save-edit-btn"
              >
                <Save className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onCancelEdit(); }}
                className="p-1 rounded-sm bg-neon-error/10 text-neon-error hover:bg-neon-error/20"
                data-testid="cancel-edit-btn"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className={`text-sm font-mono break-words leading-relaxed ${segment.target ? 'text-[#E0E0E0]' : 'text-[#64748B] italic'}`}>
              {segment.target || '— untranslated —'}
            </p>
            {segment.target && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-[10px] font-mono text-[#64748B]">
                  {segment.target_char_count} chars
                </span>
                {/* Show specific term issues if available */}
                {segment.term_issues && segment.term_issues.length > 0 ? (
                  segment.term_issues.slice(0, 3).map((issue, i) => (
                    <span key={i} className="text-[9px] font-ui text-neon-warning px-1.5 py-0.5 bg-neon-warning/10 rounded-sm border border-neon-warning/30">
                      "{issue.term_source}" → "{issue.term_target}"
                    </span>
                  ))
                ) : missingTerms.length > 0 && (
                  <span className="text-[9px] font-ui text-neon-warning px-1 py-0.5 bg-neon-warning/10 rounded-sm border border-neon-warning/30">
                    {missingTerms.length} missing term{missingTerms.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
            {hasFlags && (
              <div className="flex flex-wrap gap-1 mt-1">
                {segment.flags.map(flag => (
                  <span key={flag} className="text-[9px] font-ui text-neon-error px-1 py-0.5 bg-neon-error/10 rounded-sm border border-neon-error/30">
                    {flag.replace('_', ' ')}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Status */}
      <div>
        <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-ui font-bold uppercase tracking-wider ${badgeClass}`}>
          {statusLabel}
        </span>
      </div>

      {/* Match */}
      <div>
        <span className="text-xs font-mono text-[#94A3B8]">
          {segment.match_percentage > 0 ? `${segment.match_percentage}%` : '—'}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
        <button
          onClick={onStartEdit}
          className="p-1.5 rounded-sm hover:bg-white/5 text-[#64748B] hover:text-[#E0E0E0] transition-colors duration-200"
          title="Edit"
          data-testid={`edit-segment-${segment.id}`}
        >
          <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
        {(!segment.target || segment.status === 'new') && (
          <button
            onClick={onTranslate}
            className="p-1.5 rounded-sm hover:bg-neon-cyan/10 text-[#64748B] hover:text-neon-cyan transition-colors duration-200"
            title="Translate with AI"
            data-testid={`translate-segment-${segment.id}`}
          >
            <Wand2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="text-center">
      <p className="text-[9px] uppercase tracking-wider text-[#64748B] mb-0.5">{label}</p>
      <p className="text-lg font-heading font-bold" style={{ color: value > 0 ? color : '#64748B' }}>{value}</p>
    </div>
  );
}
